package com.anjuros.website.redesign.components

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.styles.RdFaqBandStyle
import com.anjuros.website.redesign.styles.RdFaqGridStyle
import com.anjuros.website.redesign.styles.RdFaqListStyle
import com.anjuros.website.redesign.styles.RdH2Style
import com.anjuros.website.redesign.styles.RdLedeStyle
import com.anjuros.website.redesign.styles.RdSectionHeadStyle
import com.anjuros.website.redesign.styles.RdSectionStyle
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/** Pale-blue FAQ band: heading on the left, questions on the right. The first answer starts open. */
@Composable
fun RdFaqBand(title: RdText, faqs: List<Pair<RdText, RdText>>) {
    RdSection(
        id = "faq",
        labelledBy = "faq-h",
        modifier = RdSectionStyle.toModifier().then(RdFaqBandStyle.toModifier()),
        wrapModifier = RdFaqGridStyle.toModifier(),
    ) {
        Div(RdSectionHeadStyle.toModifier().toAttrs()) {
            RdEyebrow(RdText.FAQ_EYEBROW)
            H2(RdH2Style.toModifier().id("faq-h").toAttrs()) { Text(LocalText(title)) }
            P(RdLedeStyle.toModifier().toAttrs()) {
                Text(LocalText(RdText.FAQ_MORE_START))
                RdLink(RdRoute.FAQ) { Text(LocalText(RdText.FAQ_MORE_LINK)) }
                Text(".")
            }
        }
        Div(RdFaqListStyle.toModifier().toAttrs()) {
            faqs.forEachIndexed { index, (question, answer) ->
                FaqItem(question, answer, open = index == 0)
            }
        }
    }
}

/** Native details/summary, so the answers stay in the page for search engines. */
@Composable
private fun FaqItem(question: RdText, answer: RdText, open: Boolean) {
    RdTag("details", if (open) Modifier.attrsModifier { attr("open", "") } else Modifier) {
        RdTag("summary") { Text(LocalText(question)) }
        P { RdRichText(LocalText(answer)) }
    }
}

/** FAQPage JSON-LD entries for [faqs], to drop into a page's structured data. */
@Composable
fun rdFaqJsonLd(faqs: List<Pair<RdText, RdText>>): String {
    val entries = faqs
        .map { (question, answer) -> rdPlainText(LocalText(question)) to rdPlainText(LocalText(answer)) }
        .joinToString(",") { (question, answer) ->
            """{"@type":"Question","name":${jsonString(question)},"acceptedAnswer":{"@type":"Answer","text":${jsonString(answer)}}}"""
        }
    return """{"@type":"FAQPage","mainEntity":[$entries]}"""
}
